import { useEffect, useState } from "react";
import LessonCard from "./LessonCard";
import { LessonsApiResponse } from "src/types";
import {
  fetchLessonsByLevel,
  languageMap,
} from "src/service/activities.services";
import showToast from "@utils/toast";
import { Layout } from "@components/index";
import {
  currentLevelInfo,
  generateLessonIdRange,
  getFromMemory,
  getItemWithHighestId,
  saveToMemory,
} from "@utils/storage.utils";

interface ILessonPage {
  isLoading: boolean;
  lessons: LessonsApiResponse["result"];
}

function LessonsList() {
  const [lessonList, setLessonList] = useState<ILessonPage>({
    isLoading: true,
    lessons: [],
  });

  const currentLanguage = getFromMemory("language") as string;
  const { level, levelId, title } = currentLevelInfo();

  useEffect(() => {
    if (!levelId) return;

    const fetchLessons = async () => {
      try {
        const lessons = await fetchLessonsByLevel(levelId, currentLanguage);
        if (lessons.status !== "success") {
          setLessonList({ isLoading: false, lessons: [] });
        } else {
          const lessonArr = lessons.result;
          setLessonList({ isLoading: false, lessons: lessonArr });

          // Store the last lesson ID in memory for future use
          if (lessons.result.length > 0) {
            const lessonWithHighestId = getItemWithHighestId(lessonArr);
            saveToMemory("lastLessonId", lessonWithHighestId?.id);
            saveToMemory("lessonIncr", "Yes");
          } else {
            const existingLessons = (
              await fetchLessonsByLevel(levelId, currentLanguage)
            ).result;

            // Get level id
            const levelNumber = level as number;
            const langCode = languageMap[currentLanguage];
            const newLessonId = generateLessonIdRange(
              langCode,
              levelNumber,
              existingLessons.length
            );
            saveToMemory("lastLessonId", newLessonId);
            saveToMemory("lessonIncr", "No");
          }
        }
      } catch (error: any) {
        setLessonList({ isLoading: false, lessons: [] });
        showToast(`${error?.message}`, false);
      }
    };
    fetchLessons();
  }, [levelId, currentLanguage]);

  return (
    <Layout>
      <section className="container mx-auto py-4">
        <div className="flex justify-between mb-4 mt-5">
          <h1 className="text-base font-semibold text-gray-800 mb-6">
            Lessons List
          </h1>

          <button
            onClick={() =>
              (window.location.href = `/dashboard/levels/lessons/add`)
            }
            disabled={lessonList.isLoading}
            className="bg-brand text-white px-4 py-2 rounded-md hover:bg-brand-dark transition"
          >
            {lessonList.isLoading ? "Loading Lessons...." : "New Lesson"}
          </button>
        </div>

        <p className="text-base text-black font-medium italic mb-6">
          Level Title — {title}({level})
        </p>

        {lessonList.isLoading ? (
          <p>Loading Lessons...</p>
        ) : lessonList.lessons?.length === 0 ? (
          <p>No lessons available.</p>
        ) : (
          <>
            {lessonList.lessons.map((lesson) => (
              <LessonCard key={lesson.id} lesson={lesson} />
            ))}
          </>
        )}
      </section>
    </Layout>
  );
}

export default LessonsList;
